import React from "react";
import {
  PeriodReportData,
  ReportCategory,
} from "@/api/fetchQuickBooksPeriodReport/types";

interface PeriodReportTableProps {
  report: PeriodReportData | null;
}

interface CategoryRowProps {
  category: ReportCategory;
  level: number;
  isHeader?: boolean;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatPercentage = (percent: number): string => {
  return `${(percent * 100).toFixed(2)}%`;
};

const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  level,
  isHeader = false,
}) => {
  const paddingLeft = level * 24;

  return (
    <>
      <tr
        className={`
        border-b border-gray-200 dark:border-slate-700
        ${isHeader ? "bg-gray-100 dark:bg-slate-700/50 font-semibold" : "hover:bg-gray-50 dark:hover:bg-slate-700"}
        ${level === 0 ? "border-t-2 border-gray-300 dark:border-slate-600" : ""}
      `}
      >
        <td
          className="py-3 px-4 text-gray-900 dark:text-slate-100"
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          {category.name}
          {category.accountCode && (
            <span className="text-gray-500 dark:text-slate-400 ml-2 text-sm">
              ({category.accountCode})
            </span>
          )}
        </td>
        <td className="py-3 px-4 text-right text-gray-900 dark:text-slate-100">
          {formatCurrency(category.amount)}
        </td>
        <td className="py-3 px-4 text-right text-gray-900 dark:text-slate-100">
          {formatPercentage(category.percentOfRevenue)}
        </td>
      </tr>
      {category.subcategories.map(
        (subcategory: ReportCategory, index: number) => (
          <CategoryRow
            category={subcategory}
            key={`${subcategory.name}-${index}`}
            level={level + 1}
          />
        ),
      )}
    </>
  );
};

export const PeriodReportTable: React.FC<PeriodReportTableProps> = ({
  report,
}) => {
  if (!report) {
    return null;
  }

  return (
    <div className="rounded-md border border-gray-200 dark:border-slate-700 overflow-x-auto bg-white dark:bg-slate-800">
      <table className="min-w-full text-sm text-gray-900 dark:text-slate-100">
        <thead>
          <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
            <th
              className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider"
              scope="col"
            >
              Account
            </th>
            <th
              className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider"
              scope="col"
            >
              Amount
            </th>
            <th
              className="py-3 px-4 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider"
              scope="col"
            >
              % of Revenue
            </th>
          </tr>
        </thead>
        <tbody>
          <CategoryRow category={report.revenue} isHeader level={0} />
          <CategoryRow category={report.costOfGoodsSold} isHeader level={0} />
          <CategoryRow category={report.grossProfit} isHeader level={0} />
          <CategoryRow category={report.operatingExpenses} isHeader level={0} />
          <CategoryRow category={report.operatingIncome} isHeader level={0} />
          <CategoryRow
            category={report.otherIncomeExpenses}
            isHeader
            level={0}
          />
          <CategoryRow category={report.netIncome} isHeader level={0} />
        </tbody>
      </table>
    </div>
  );
};
